import React, { useMemo, useRef, useLayoutEffect } from "react";
import * as THREE from "three";
import { useStore } from "../store";
import { POS, TOWER } from "./layout";

const FLOORS = {
  f107: { pos: POS.northPool, y: 400, label: "North Tower · Floor 107" },
  f81: { pos: POS.southPool, y: 300, label: "South Tower · Floor 81" },
  roof: { pos: POS.southPool, y: 419, label: "South Tower · Roof — Top of the World" },
};

// Open-air South Tower rooftop: glass partitions + metal railings, full 360° sky.
function RoofDeck() {
  const f = FLOORS.roof;
  const size = 52;
  const half = size / 2;
  const sides = [
    [0, half, 0],
    [0, -half, 0],
    [half, 0, Math.PI / 2],
    [-half, 0, Math.PI / 2],
  ];
  return (
    <group position={[f.pos[0], f.y, f.pos[2]]}>
      <mesh position={[0, -0.2, 0]} receiveShadow>
        <boxGeometry args={[size, 0.5, size]} />
        <meshStandardMaterial color="#4a4f55" metalness={0.4} roughness={0.6} />
      </mesh>
      {/* waist-high glass partitions */}
      {sides.map((g, i) => (
        <mesh key={`g${i}`} position={[g[0], 0.75, g[1]]} rotation={[0, g[2], 0]}>
          <planeGeometry args={[size, 1.5]} />
          <meshPhysicalMaterial color="#bfe0ff" transparent opacity={0.16} roughness={0.05} metalness={0} transmission={0.6} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* metallic top railing + corner posts */}
      {sides.map((g, i) => (
        <mesh key={`r${i}`} position={[g[0], 1.5, g[1]]} rotation={[0, g[2], 0]}>
          <boxGeometry args={[size, 0.14, 0.14]} />
          <meshStandardMaterial color="#c9ced1" metalness={0.85} roughness={0.25} />
        </mesh>
      ))}
      {[[half, half], [half, -half], [-half, half], [-half, -half]].map((p, i) => (
        <mesh key={`p${i}`} position={[p[0], 0.75, p[1]]}>
          <cylinderGeometry args={[0.12, 0.12, 1.5, 8]} />
          <meshStandardMaterial color="#c9ced1" metalness={0.85} roughness={0.25} />
        </mesh>
      ))}
      <mesh position={[0, 0.12, 0]}>
        <ringGeometry args={[3, 3.6, 32]} />
        <meshBasicMaterial color="#D4AF37" side={THREE.DoubleSide} />
      </mesh>
      <pointLight position={[0, 3, 0]} intensity={1.2} distance={70} decay={1.4} color="#fff2d6" />
    </group>
  );
}

// Observation deck "Viewing Cube" with trident window frames + translucent glass.
function ViewingCube({ floor }) {
  const f = FLOORS[floor];
  const size = 50;
  const h = 6;
  const mullRef = useRef();
  const step = 2.4;
  const nPer = Math.floor(size / step);

  useLayoutEffect(() => {
    if (!mullRef.current) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    let idx = 0;
    const half = size / 2;
    for (let side = 0; side < 4; side++) {
      for (let i = 0; i <= nPer; i++) {
        const o = -half + i * step;
        let x = 0,
          z = 0,
          rot = 0;
        if (side === 0) { x = o; z = half; }
        else if (side === 1) { x = o; z = -half; }
        else if (side === 2) { x = half; z = o; rot = Math.PI / 2; }
        else { x = -half; z = o; rot = Math.PI / 2; }
        q.setFromAxisAngle(up, rot);
        m.compose(new THREE.Vector3(x, h / 2, z), q, new THREE.Vector3(1, 1, 1));
        mullRef.current.setMatrixAt(idx++, m);
      }
    }
    mullRef.current.count = idx;
    mullRef.current.instanceMatrix.needsUpdate = true;
  }, [floor, nPer]);

  const maxCount = (nPer + 1) * 4;

  return (
    <group position={[f.pos[0], f.y, f.pos[2]]}>
      {/* floor */}
      <mesh position={[0, -0.1, 0]} receiveShadow>
        <boxGeometry args={[size + 2, 0.4, size + 2]} />
        <meshStandardMaterial color="#3a3f45" roughness={0.7} metalness={0.3} />
      </mesh>
      {/* ceiling */}
      <mesh position={[0, h, 0]}>
        <boxGeometry args={[size + 2, 0.4, size + 2]} />
        <meshStandardMaterial color="#2c3036" roughness={0.8} />
      </mesh>
      {/* translucent glass panes (4 sides) */}
      {[
        [0, h / 2, size / 2, size, 0],
        [0, h / 2, -size / 2, size, 0],
        [size / 2, h / 2, 0, size, Math.PI / 2],
        [-size / 2, h / 2, 0, size, Math.PI / 2],
      ].map((g, i) => (
        <mesh key={i} position={[g[0], g[1], g[2]]} rotation={[0, g[4], 0]}>
          <planeGeometry args={[g[3], h]} />
          <meshPhysicalMaterial
            color="#bfe0ff"
            transparent
            opacity={0.12}
            roughness={0.05}
            metalness={0}
            transmission={0.6}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* trident vertical mullions */}
      <instancedMesh ref={mullRef} args={[null, null, maxCount]}>
        <boxGeometry args={[0.5, h, 0.5]} />
        <meshStandardMaterial color="#d3d7d9" metalness={0.7} roughness={0.3} />
      </instancedMesh>
      {/* deck marker glow */}
      <mesh position={[0, 0.2, 0]}>
        <ringGeometry args={[3, 3.6, 32]} />
        <meshBasicMaterial color="#D4AF37" side={THREE.DoubleSide} />
      </mesh>
      <pointLight position={[0, h - 1, 0]} intensity={1.6} distance={90} decay={1.4} color="#fff2d6" />
      <ambientLight intensity={0.35} />
    </group>
  );
}

export default function Interiors() {
  const navMode = useStore((s) => s.navMode);
  const interiorFloor = useStore((s) => s.interiorFloor);
  if (navMode !== "interior") return null;
  if (interiorFloor === "roof") return <RoofDeck />;
  return <ViewingCube floor={interiorFloor} />;
}
